import React, { createContext, useContext } from 'react';
import { useAccount } from '../account/AccountContexts';
import { MonitorType } from '../../types/monitor/MonitorType';
import { Addons } from '../../types/account/Addons';

// Effective feature-gating (models + addons) for the current project view. `undefined` = no provider
// (fall back to the logged-in account's own config).
interface EffectiveConfig {
  features: MonitorType[];
  addons: Addons[];
}

const EffectiveFeaturesContext = createContext<EffectiveConfig | undefined>(undefined);

// The owner's config (project.ownerFeatures/ownerAddons) arrives as plain string[]; cast to the account
// types here so consumers get the same shape as accountResponse.features/addons.
export const EffectiveFeaturesProvider: React.FC<{
  features: string[];
  addons: string[];
  children: React.ReactNode;
}> = ({ features, addons, children }) => (
  <EffectiveFeaturesContext.Provider
    value={{ features: features as MonitorType[], addons: addons as Addons[] }}
  >
    {children}
  </EffectiveFeaturesContext.Provider>
);

// Which models to show for the current project. Inside a project preview this is the OWNER's features
// when a superadmin views a client's project; everywhere else it falls back to the logged-in account's
// features, so shared components (FilterPanel, PromptsTable, …) keep working unchanged.
export const useEffectiveFeatures = (): MonitorType[] => {
  const ctx = useContext(EffectiveFeaturesContext);
  const { accountResponse } = useAccount();
  return ctx?.features ?? accountResponse?.features ?? [];
};

// Effective addons (e.g. NEW_STATS) for the current project view — owner's on a client's project,
// otherwise the logged-in account's. Keeps NEW_STATS-gated logic consistent with the shown version.
export const useEffectiveAddons = (): Addons[] => {
  const ctx = useContext(EffectiveFeaturesContext);
  const { accountResponse } = useAccount();
  return ctx?.addons ?? accountResponse?.addons ?? [];
};
